import type { AlignmentState } from "@cuekards/aligner";
import type { CaptureCommand, RecordingPhase } from "@cuekards/core/recording";
import type { NudgeDirection } from "@cuekards/core/script-model";
import type { FollowerState } from "@cuekards/core/speech";
import type { VoiceCommandState } from "@cuekards/core/voice-commands";
import { messageKind, type MessagePortLike } from "./protocol.js";

/**
 * The line between the core worker and the recognition worker (web ticket 14).
 *
 * The desktop keeps the recogniser and every controller in one process, so nothing
 * corresponding exists there. **ADR-0011 is the argument for why this line exists
 * at all** and why `FollowerController` is on the far side of it rather than in the
 * core worker where the spec puts every controller; what is recorded here is the
 * shape that decision produces.
 *
 * What crosses is not audio and not positions on demand — it is the controller's
 * own ports, reversed: what the core has to *tell* the follower, and what the
 * follower has to tell the core.
 *
 * It is deliberately not the renderer-to-main contract. That contract is the shape
 * a *view* talks to a host in, and these two are both hosts; two of the four
 * messages coming back (`aligned`, `resynced`) are not channels on it at all, and
 * inventing channels for them would be adding to a contract whose whole value is
 * that it did not change.
 *
 * Audio does not travel on this link either — see {@link SpeechFrameMessage}.
 */

/** What the core worker tells the recognition worker. */
export type ToFollower =
  /** A script has loaded or been refreshed: rebuild the recogniser against it,
   * which is what compiles the vocabulary biasing (FR-38). */
  | { kind: "script"; text: string }
  /** The user has put the anchor somewhere by hand — move the matching window
   * with them (FR-36). */
  | { kind: "seek"; position: number }
  /**
   * The constraints for the currently selected microphone (FR-42).
   *
   * Pushed rather than asked for, because `FollowerPorts.micConstraints` is
   * synchronous and the device selection lives in the core worker. Sent whenever
   * the selection changes, so the value waiting here is always the current one
   * by the time rehearsal opens a device with it.
   */
  | { kind: "microphone"; audio: MediaTrackConstraints }
  /** Start or stop following with nothing recording (FR-37). */
  | { kind: "toggle-rehearsal" }
  /** Force a match against a widened window (FR-36). */
  | { kind: "resync" }
  /**
   * A capture event from the page, forwarded unvalidated.
   *
   * The recorder and the fork are two consumers of one microphone, so they are
   * two consumers of one event stream — and on the desktop both controllers sit
   * in main and see the same send. Here one of them is a worker away, so the
   * core passes it on; each still ignores what is not its own.
   */
  | { kind: "capture-event"; event: unknown }
  /**
   * The voice-command switch (story 40), forwarded unvalidated for the reason
   * `capture-event` is: `VoiceCommandController` is on the far side and is where
   * the validation lives, and a value coerced on the way past would be a second
   * opinion about what "not a boolean" means. The safe reading of a bad value
   * there is "not listening".
   *
   * Pushed rather than asked for, like {@link ToFollower} `microphone` and for the
   * same reason: the controller reads it synchronously and the setting that holds
   * it lives in the core worker. Sent on the switch changing and once on connect,
   * which is what restores it after a reload.
   */
  | { kind: "voice-enabled"; enabled: unknown }
  /**
   * Where the take is, so a spoken stop knows whether there is anything to
   * confirm.
   *
   * The *phase* rather than the answer, so that the rule about which phases a
   * spoken stop can still act on stays in one place — `stopIsWorthConfirming`,
   * which both hosts read.
   */
  | { kind: "recording-phase"; phase: RecordingPhase };

/**
 * A transport call a recognised command asks for (story 39).
 *
 * Exactly the four `VoiceCommandPorts` makes on the recording engine, named for the
 * calls rather than for the words that produced them — the link is the controller's
 * own ports, reversed, and these are four of them. The fifth, the nudge, travels as
 * a direction on its own message for the same reason.
 *
 * There is no `confirm`. Confirming is not something the transport hears about; it is
 * what turns a pending stop into `stop`, inside the controller.
 */
export type VoiceAction = "start" | "pause" | "resume" | "stop";

/** What the recognition worker tells the core worker. */
export type FromFollower =
  /** The follower's visible state, which the core broadcasts and reads the
   * prompter's `speechUnavailable` out of. */
  | { kind: "follower-state"; state: FollowerState }
  /** Where the follower believes the speaker is, on every frame. Whether it
   * moves the prompter is the scroll mode's decision, not the follower's. */
  | { kind: "aligned"; state: AlignmentState }
  /** The result of a re-sync, which applies in every mode (FR-36). */
  | { kind: "resynced"; state: AlignmentState }
  /** A request to the microphone the page owns — rehearsal opening and closing
   * the device. */
  | { kind: "capture-command"; command: CaptureCommand }
  /** The voice-command state both the panel and the prompter are views onto — the
   * switch, why it will not arm for this script, and whether a stop is waiting to
   * be confirmed. Broadcast by the core and cached there, because the prompter
   * pulls it on mount and has to be answered synchronously. */
  | { kind: "voice-state"; state: VoiceCommandState }
  /**
   * Remember the switch, having accepted it.
   *
   * Sent back rather than persisted by the core on the way in, so that what is
   * stored is exactly what the controller took: it is the one thing that decides
   * whether a value crossing from a page counts as a switch at all, and the store
   * should not hold a value it refused.
   */
  | { kind: "voice-save-enabled"; enabled: boolean }
  /** A recognised command, for the transport to act on (story 39). */
  | { kind: "voice-act"; action: VoiceAction }
  /** A spoken `next` or `back`, as the step it asked for — the same `overlay.nudge`
   * a keypress lands on, which is why it is a direction and not a word. */
  | { kind: "voice-nudge"; direction: NudgeDirection };

/**
 * One frame of forked microphone audio, page to recognition worker (FR-31).
 *
 * Not on the link and not on the contract: this is the fork itself, and it goes
 * the short way. The samples' buffer is transferred rather than copied, which is
 * safe because the audio worklet hands the page a fresh copy of every frame.
 */
export interface SpeechFrameMessage {
  kind: "speech-frame";
  samples: Float32Array;
}

/**
 * The handover: one end of a {@link MessageChannel}, posted to each worker by
 * the page that made it.
 *
 * The page is the only context that can introduce two workers to each other, and
 * a port has to be transferred rather than cloned — so the link cannot be a
 * constructor argument on either side, and both ends wait for it to arrive.
 */
export interface SpeechLinkMessage {
  kind: "speech-link";
  port: MessagePortLike;
}

export const SPEECH_LINK = "speech-link";
export const SPEECH_FRAME = "speech-frame";

/**
 * A port that can hand a transferable over.
 *
 * Narrower than {@link MessagePortLike} on purpose: only the page ever transfers
 * anything, so only the page's two sends need the second argument, and keeping it
 * out of the shared port shape leaves that shape satisfied by everything it is
 * satisfied by today.
 */
export interface TransferringPort {
  postMessage(message: unknown, transfer: readonly unknown[]): void;
}

/** Post a link handover down `port`, transferring the channel end with it. */
export function sendSpeechLink(port: TransferringPort, end: unknown): void {
  port.postMessage({ kind: SPEECH_LINK, port: end }, [end]);
}

/**
 * Wait for the link handover on `port` and hand over the end that arrives.
 *
 * Both workers use this, because both are on the receiving end of the same
 * introduction. Called once at startup: a second link would be a second
 * follower, which is a mistake rather than a configuration.
 *
 * Returns the unsubscribe, so a host that is torn down stops listening for a
 * link it will no longer wire up.
 */
export function awaitSpeechLink(
  port: MessagePortLike,
  attach: (link: MessagePortLike) => void,
): () => void {
  const receive = (event: { data: unknown }): void => {
    if (messageKind(event.data) !== SPEECH_LINK) return;
    const link = (event.data as SpeechLinkMessage).port;
    // Anything that is not a port cannot be linked to, and a page that posted
    // one is the only thing that ever sends this.
    if (typeof link?.postMessage !== "function") return;
    port.removeEventListener("message", receive);
    // Not started here: `onLinkMessage` starts it once a listener is on it, which
    // is the only order in which nothing can be delivered into no handler.
    attach(link);
  };
  port.addEventListener("message", receive);
  port.start?.();
  return () => port.removeEventListener("message", receive);
}

/** The kinds the recognition worker accepts, so its listener can be built from
 * the type rather than from a hand-kept copy of it. */
export const TO_FOLLOWER_KINDS = [
  "script",
  "seek",
  "microphone",
  "toggle-rehearsal",
  "resync",
  "capture-event",
  "voice-enabled",
  "recording-phase",
] as const satisfies readonly ToFollower["kind"][];

/** The kinds the core worker accepts back. */
export const FROM_FOLLOWER_KINDS = [
  "follower-state",
  "aligned",
  "resynced",
  "capture-command",
  "voice-state",
  "voice-save-enabled",
  "voice-act",
  "voice-nudge",
] as const satisfies readonly FromFollower["kind"][];
